import { useDirtyData } from "hooks/useDirtyData/useDirtyData";
import { DefaultEventUpdateRequest, DefaultFleetEventModel, DefaultStepsConfig } from "pages/asset/events/modals/DefaultModels";
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { formatDate } from "pages/asset/events/shared/utils";
import { useSelector } from 'react-redux';

const noop = (_props, _args) => { };

const EventWizardContext = createContext({
    loadingData: false,
    setLoadingData: noop,
    saving: false,
    setSaving: noop,
    event: DefaultFleetEventModel,
    setEvent: noop,
    eventStatus: {},
    setEventStatus: noop,
    eventType: {},
    setEventType: noop,
    eventUpdateRequest: DefaultEventUpdateRequest,
    setEventUpdateRequest: noop,
    originalEventUpdateRequest: null,
    setOriginalEventUpdateRequest: noop,
    stepIsValid: true,
    setStepIsValid: noop,
    isEventDataDirty: false,
    saveOnEventUpdateRequest: false,
    setSaveOnEventUpdateRequest: noop,
    eventOptions: {},
    setEventOptions: noop,
    onEventUpdateRequestChange: noop,
    canProceed: false,
    stepsConfig: DefaultStepsConfig,
    setStepsConfig: noop,
    specialEventTypeRestricted: false,
    tempDefectList: [],
    setTempDefectList: noop,
    tempCostList: [],
    setTempCostList: noop,
    completeButtonText: '',
    setCompleteButtonText: noop,
    hasMadeChanges: false,
    setHasMadeChanges: noop,
    canFinishEvent: false,
    setCanFinishEvent: noop
});

export function EventWizardProvider({ children }) {
    const userId = useSelector((state) => state.loggedInUser.userId);

    const [loadingData, setLoadingData] = useState(false);
    const [saving, setSaving] = useState(false);
    const [stepIsValid, setStepIsValid] = useState(true);
    // added to support reacting to changes made in documents, costs, etc.
    const [hasMadeChanges, setHasMadeChanges] = useState(false);
    const [saveOnEventUpdateRequest, setSaveOnEventUpdateRequest] = useState(false);
    const [stepsConfig, setStepsConfig] = useState(DefaultStepsConfig);
    const [specialEventTypeRestricted, setSpecialEventTypeRestricted] = useState(false);
    const [activeStep, setActiveStep] = useState("");
    const [canSave, setCanSave] = useState(false);
    const [canFinishEvent, setCanFinishEvent] = useState(false);

    const [tempDefectList, setTempDefectList] = useState([]);
    const [tempCostList, setTempCostList] = useState([]);

    // API Data
    const [event, setEvent] = useState(DefaultFleetEventModel);
    const [eventStatus, setEventStatus] = useState({});
    const [eventUpdateRequest, setEventUpdateRequest] = useState(DefaultEventUpdateRequest);
    const [originalEventUpdateRequest, setOriginalEventUpdateRequest] = useState(null);
    const [eventType, setEventType] = useState({});
    const [eventOptions, setEventOptions] = useState({});
    const [completeButtonText, setCompleteButtonText] = useState('');

    const isEventDataDirty = useDirtyData({
        ...originalEventUpdateRequest,
        costs: event?.costs ?? [],
        defects: event?.defects ?? []
    }, {
        ...eventUpdateRequest,
        costs: tempCostList,
        defects: tempDefectList
    });

    const onEventUpdateRequestChange = useCallback((updatedDetails, saveAfterChange = false) => {
        setEventUpdateRequest((prev) => ({
            ...prev,
            ...updatedDetails,
            eventDate: formatDate(updatedDetails.eventDate, prev.eventDate),
            vorStartDate: formatDate(updatedDetails.vorStartDate, prev.vorStartDate),
            vorEndDate: formatDate(updatedDetails.vorEndDate, prev.vorEndDate),
        }));

        if (saveAfterChange) {
            setSaveOnEventUpdateRequest(true);
        }
    }, []);

    const resheduleEventConfig = useMemo(() => {
        const resheduleEvent = {
            ...stepsConfig.ResheduleEvent,
        };

        resheduleEvent.rules = [];

        resheduleEvent.rules.push({
            fieldName: "eventDate",
            required: true,
        });

        if (eventType.vorTimer || (eventType.vorTimerFailedOnly && eventUpdateRequest.failed)) {
            resheduleEvent.rules.push(
                {
                    fieldName: "vorStartDate",
                    required: true
                },
                {
                    fieldName: "vorEndDate",
                    required: true
                }
            );
        }

        if (eventType.specialProcessingKey) {
            if (eventType.specialProcessingKey.toUpperCase() === 'MOT' || eventType.specialProcessingKey.toUpperCase() === 'TAX') {
                setSpecialEventTypeRestricted(true);
            }
            if (eventType.specialProcessingKey.toUpperCase() === 'PMI') {
                resheduleEvent.alwaysMoveFutureEvents = true;
            }
        }

        return resheduleEvent;
    }, [eventType, eventUpdateRequest]);

    const commentFailEventConfig = useMemo(() => {
        const commentFailEvent = {
            ...stepsConfig.CommentFailEvent
        };

        commentFailEvent.fields = [];

        if (eventType?.failStatusEnabled && (!eventType?.specialProcessingKey || eventType.specialProcessingKey.toUpperCase() !== 'MOT')) {
            commentFailEvent.fields.push('hasEventFail');
        }

        if (eventType?.includeComment || eventType?.requiresComment || eventType?.requiresCommentFailedOnly) {
            commentFailEvent.fields.push('comment');
        }

        commentFailEvent.skipStep = !eventStatus?.startedStatus || commentFailEvent.fields.length === 0;

        commentFailEvent.commentIsRequired = !!((eventType?.requiresCommentFailedOnly && eventUpdateRequest?.failed) || eventType?.requiresComment);

        if (!commentFailEvent.commentIsRequired || (eventUpdateRequest.comment && eventUpdateRequest.comment.length > 0)) {
            commentFailEvent.stepCompleted = true;
        } else {
            commentFailEvent.stepCompleted = false;
        }

        return commentFailEvent;
    }, [eventType, eventUpdateRequest, stepsConfig.CommentFailEvent?.addCommentLater]);

    const defectEventConfig = useMemo(() => {
        const defectEvent = {
            ...stepsConfig.DefectEvent
        };

        defectEvent.skipStep = !eventStatus?.startedStatus || !eventType?.includeDefect;
        defectEvent.defectIsRequired = eventType?.requiresDefect || (eventUpdateRequest?.failed && eventType?.requiresDefectFailedOnly);

        defectEvent.hasDefects = event && event.defects.length > 0;
        defectEvent.notSignedOffDefect = defectEvent.hasDefects && (event.defects.filter(d => d.rectified === null).length > 0);

        //Defects must be signed off even if they're not required.
        if ((!defectEvent.defectIsRequired || defectEvent.hasDefects) && !defectEvent.notSignedOffDefect) {
            defectEvent.stepCompleted = true;
        } else {
            defectEvent.stepCompleted = false;
        }

        return defectEvent;
    }, [eventType, eventUpdateRequest, event]);

    const costEventConfig = useMemo(() => {
        const costEvent = {
            ...stepsConfig.CostEvent
        };

        costEvent.skipStep = !eventStatus?.startedStatus || !eventType?.includeCosts;
        costEvent.costIsRequired = eventType?.requiresCosts || (eventUpdateRequest?.failed && eventType?.requiresCostsFailedOnly);

        if (!costEvent.costIsRequired || (event.costs && event.costs.length > 0)) {
            costEvent.stepCompleted = true;
        } else {
            costEvent.stepCompleted = false;
        }

        return costEvent;
    }, [eventType, eventUpdateRequest, event]);

    const costDocumentEventConfig = useMemo(() => {
        const costDocumentEvent = {
            ...stepsConfig.CostDocumentEvent
        };

        //If there is no cost we don't want cost documentation
        costDocumentEvent.skipStep = !eventStatus?.startedStatus || !eventType?.includeCostDocumentation
            || (eventType?.requiresCostsFailedOnly && !eventUpdateRequest?.failed);

        const costDocuments = event?.documents?.filter(doc => doc.type.toLowerCase() === "cost") || [];

        costDocumentEvent.documentsAreRequired = eventType?.includeCostDocumentation && eventType?.requiresCostDocumentationCount && eventType.requiresCostDocumentationCount > 0;

        if (costDocumentEvent.skipStep || (!costDocumentEvent.documentsAreRequired || costDocuments.length >= eventType.requiresCostDocumentationCount)) 
        {
            costDocumentEvent.stepCompleted = true;
        } else {
            costDocumentEvent.stepCompleted = false;
        }

        return costDocumentEvent;
    }, [eventType, eventUpdateRequest, event])

    const documentEventConfig = useMemo(() => {
        const documentEvent = {
            ...stepsConfig.DocumentEvent
        };

        documentEvent.skipStep = !eventStatus?.startedStatus || !eventType.includeDocumentation;

        const eventDocuments = event?.documents?.filter(doc => doc.type.toLowerCase() === "event") || [];

        //includeDocumentationFailedOnly is really a misnomer, it is being used for requires on Failed rather than include.
        documentEvent.documentsAreRequired = eventType?.requiresDocumentationCount && eventType.requiresDocumentationCount > 0 && (!eventType.includeDocumentationFailedOnly || eventUpdateRequest?.failed);

        if (documentEvent.skipStep || !documentEvent.documentsAreRequired ||
            (documentEvent.documentsAreRequired && eventDocuments.length >= eventType.requiresDocumentationCount)) {
            documentEvent.stepCompleted = true;
        } else {
            documentEvent.stepCompleted = false;
        }

        return documentEvent;
    }, [eventType, eventUpdateRequest, event]);

    const signoffEventConfig = useMemo(() => {
        const signoffEvent = {
            ...stepsConfig.SignoffEvent
        };

        const signoffRequirements = ["signoff","second signoff"];
        const filteredOutstandingRequirements = event?.outstandingRequirements?.filter(r => !signoffRequirements.includes(r.toLowerCase()));

        signoffEvent.signoffRequired = eventType.requiresSignoff || (eventType.requiresSignoffFailedOnly && eventUpdateRequest?.failed);
        //We only want to show signoff if it's required.
        signoffEvent.skipStep = !eventStatus?.startedStatus || !signoffEvent.signoffRequired || filteredOutstandingRequirements?.length > 0;
        signoffEvent.secondSignoffRequired = eventType.signoffSecondEnabled && signoffEvent.signoffRequired;
        signoffEvent.signoffRestricted = eventType.signoffRestricted === true && event?.userSignoffRestricted === true;
        signoffEvent.secondSignoffRestricted = eventType.signoffSecondRestricted === true && event?.userSignoffRestricted === true;
        signoffEvent.hasBeenSignedOff = event.signoffDate !== null && event.signoffDate !== undefined;
        signoffEvent.showSecondSignoff = eventType?.signoffSecondEnabled === true && signoffEvent.hasBeenSignedOff;
        signoffEvent.hasBeenSecondSignedOff = event.signoffSecondDate !== null && event.signoffSecondDate !== undefined;
        signoffEvent.hasUserAlreadySignedOff = userId === event?.signoffUserId;
        signoffEvent.canComplete = signoffEvent.signoffRequired && !signoffEvent.secondSignoffRequired || signoffEvent.signoffRequired && signoffEvent.showSecondSignoff;
        // signoff step complete if signoff is skipped or if the event has been signed off 
        // and second signoff is not enabled or second signoff has been done 
        signoffEvent.stepCompleted = signoffEvent.skipStep || signoffEvent.hasBeenSignedOff && (!eventType.signoffSecondEnabled || signoffEvent.hasBeenSecondSignedOff);

        return signoffEvent;
    }, [eventType, eventUpdateRequest, event, userId]);

    const checkEventConfig = useMemo(() => {
        const checkEvent = {
            ...stepsConfig.CheckEvent
        };

        if (!eventType?.id || eventType.id < 1) {
            return checkEvent;
        }

        checkEvent.checkRequired = eventType.relatedTemplates && eventType.relatedTemplates.length > 0;
        if (checkEvent.checkRequired) {
            let eventTypeTemplateIds = eventType.relatedTemplates.map(t => t.templateId);
            let someChecksExist = event.checks && event.checks.length > 0;

            if (someChecksExist) {
                let eventTemplateIds = event.checks.map(t => t.templateId);
                checkEvent.checksExist = eventTypeTemplateIds.every(v => eventTemplateIds.includes(v));
            }
        }

        return checkEvent;
    }, [eventType, eventUpdateRequest, event]);

    useEffect(() => {

        if (event?.costs && event.costs.length > 0) {
            setTempCostList(event.costs);
        }

        if (event?.defects && event.defects.length > 0) {
            setTempDefectList(event.defects);
        }
    }, [event?.costs, event?.defects]);


    // Initial setup for steps
    useEffect(() => {
        let arr = [!resheduleEventConfig.skipStep, !commentFailEventConfig.skipStep, !defectEventConfig.skipStep, !costEventConfig.skipStep, !documentEventConfig.skipStep, !costDocumentEventConfig.skipStep, !signoffEventConfig.skipStep];
        let totalSteps = arr.filter(Boolean).length;
        
        setStepsConfig(prev => ({
            ...prev,
            TotalSteps: totalSteps,
            ResheduleEvent: resheduleEventConfig,
            CommentFailEvent: commentFailEventConfig,
            DefectEvent: defectEventConfig,
            CostEvent: costEventConfig,
            CostDocumentEvent: costDocumentEventConfig,
            DocumentEvent: documentEventConfig,
            SignoffEvent: signoffEventConfig,
            CheckEvent: checkEventConfig
        }));
    }, [resheduleEventConfig, commentFailEventConfig, defectEventConfig, costEventConfig, documentEventConfig, costDocumentEventConfig, signoffEventConfig, checkEventConfig])

    useEffect(() => {
        const tempCommentStepCompleted = stepsConfig.CommentFailEvent.skipStep || stepsConfig.CommentFailEvent.stepCompleted;
        const tempDefectStepCompleted = stepsConfig.DefectEvent.skipStep || stepsConfig.DefectEvent.stepCompleted;
        const tempCostStepCompleted = stepsConfig.CostEvent.skipStep || stepsConfig.CostEvent.stepCompleted;
        const tempCostDocumentStepCompleted = stepsConfig.CostDocumentEvent.skipStep || stepsConfig.CostDocumentEvent.stepCompleted;
        const tempDocumentStepCompleted = stepsConfig.DocumentEvent.skipStep || stepsConfig.DocumentEvent.stepCompleted;
        const tempSignoffStepCompleted = stepsConfig.SignoffEvent.skipStep || stepsConfig.SignoffEvent.stepCompleted;
        const tempCheckStepCompleted = !stepsConfig.CheckEvent.checkRequired || stepsConfig.CheckEvent.checksExist;

        //This is where to check the Event is ready to be completed, specialEventTypeRestricted should not be able to complete this from the front end.
        if (
            !specialEventTypeRestricted &&
            eventStatus?.startedStatus &&
            tempCommentStepCompleted &&
            tempDefectStepCompleted &&
            tempCostStepCompleted &&
            tempCostDocumentStepCompleted &&            
            tempDocumentStepCompleted &&
            tempSignoffStepCompleted &&
            tempCheckStepCompleted
        ) {
            if (eventUpdateRequest?.failed) {
                setCompleteButtonText('Complete with Fail');
            } else {
                setCompleteButtonText('Complete and Close');
            }
        } 
        else {
            setCompleteButtonText('');
        }

        // can finish event controls the normal save and close button, it needs to evaluate the add later radio buttons
        setCanFinishEvent(
            eventStatus?.startedStatus &&
            (stepsConfig.CommentFailEvent.addCommentLater || tempCommentStepCompleted) &&
            (stepsConfig.DefectEvent.signoffDefectLater || tempDefectStepCompleted) &&
            (stepsConfig.CostEvent.addCostLater || tempCostStepCompleted) &&
            (stepsConfig.CostDocumentEvent.addCostDocumentsLater || tempCostDocumentStepCompleted) &&
            (stepsConfig.DocumentEvent.addDocumentsLater || tempDocumentStepCompleted) &&
            (stepsConfig.SignoffEvent.signoffLater || tempSignoffStepCompleted)
        );
    }, [stepsConfig, eventStatus, specialEventTypeRestricted, eventUpdateRequest, eventType]);

    const value = useMemo(() => ({
        loadingData,
        setLoadingData,
        event,
        setEvent,
        eventStatus,
        setEventStatus,
        eventUpdateRequest,
        setEventUpdateRequest,
        originalEventUpdateRequest,
        setOriginalEventUpdateRequest,
        eventType,
        setEventType,
        saving,
        setSaving,
        stepIsValid,
        setStepIsValid,
        isEventDataDirty,
        saveOnEventUpdateRequest,
        setSaveOnEventUpdateRequest,
        onEventUpdateRequestChange,
        eventOptions,
        setEventOptions,
        stepsConfig,
        setStepsConfig,
        specialEventTypeRestricted,
        tempDefectList,
        setTempDefectList,
        tempCostList,
        setTempCostList,
        setCompleteButtonText,
        completeButtonText,
        hasMadeChanges,
        setHasMadeChanges,
        activeStep,
        setActiveStep,
        canSave,
        setCanSave,
        canFinishEvent
    }), [
        loadingData,
        event,
        eventStatus,
        eventUpdateRequest,
        originalEventUpdateRequest,
        eventType,
        saving,
        stepIsValid,
        isEventDataDirty,
        saveOnEventUpdateRequest,
        eventOptions,
        stepsConfig,
        specialEventTypeRestricted,
        tempDefectList,
        tempCostList,
        onEventUpdateRequestChange,
        completeButtonText,
        hasMadeChanges,
        activeStep,
        canSave,
        canFinishEvent
    ]);
    return (
        <EventWizardContext.Provider value={value}>
            {children}
        </EventWizardContext.Provider>
    );
}

// Custom hook to use the context
export function useEventWizard() {
    return useContext(EventWizardContext);
}